import { detectPlatform } from "../platform.js";

/**
 * Platform-aware text file I/O — the ONLY place that touches the file system /
 * download APIs. On Tauri it dynamically imports the dialog + fs plugins (the
 * same `/* @vite-ignore *​/` indirection as the sql adapter, so Vite doesn't try
 * to resolve them in the browser build). On Capacitor (Android) saves go
 * through a cache file + the system SHARE SHEET — the WebView ignores
 * anchor-`download`, so a silent no-op otherwise; opens use the browser file
 * input, which Android backs with the system picker. In the browser it falls
 * back to a Blob download / a hidden file input.
 */

export interface FileKind {
  /** Human label shown in the native dialog (e.g. "JSON", "iCalendar"). */
  name: string;
  /** Extensions without the dot (e.g. ["json"], ["ics"]). */
  extensions: string[];
  /** MIME type for the browser Blob download / file input accept. */
  mime: string;
}

export async function saveTextFile(text: string, defaultName: string, kind: FileKind): Promise<void> {
  if (detectPlatform() === "tauri") {
    const dialog = await importTauri<{ save(opts: unknown): Promise<string | null> }>("@tauri-apps/plugin-dialog");
    const fs = await importTauri<{ writeTextFile(path: string, contents: string): Promise<void> }>("@tauri-apps/plugin-fs");
    const path = await dialog.save({ defaultPath: defaultName, filters: [{ name: kind.name, extensions: kind.extensions }] });
    if (path) await fs.writeTextFile(path, text);
    return;
  }
  if (detectPlatform() === "capacitor") {
    const { Filesystem, Directory, Encoding } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const res = await Filesystem.writeFile({ path: defaultName, data: text, directory: Directory.Cache, encoding: Encoding.UTF8 });
    try {
      await Share.share({ title: defaultName, files: [res.uri] });
    } catch {
      // Dismissing the share sheet rejects — that's a cancel, not an error.
    }
    return;
  }
  downloadInBrowser(text, defaultName, kind.mime);
}

export async function openTextFile(kind: FileKind): Promise<string | null> {
  if (detectPlatform() === "tauri") {
    const dialog = await importTauri<{ open(opts: unknown): Promise<string | null> }>("@tauri-apps/plugin-dialog");
    const fs = await importTauri<{ readTextFile(path: string): Promise<string> }>("@tauri-apps/plugin-fs");
    const path = await dialog.open({ multiple: false, filters: [{ name: kind.name, extensions: kind.extensions }] });
    if (typeof path !== "string") return null;
    return fs.readTextFile(path);
  }
  return pickInBrowser(kind);
}

/** Like {@link openTextFile} but allows picking several files; returns their
 *  contents. null = the dialog was cancelled. */
export async function openTextFiles(kind: FileKind): Promise<string[] | null> {
  if (detectPlatform() === "tauri") {
    const dialog = await importTauri<{ open(opts: unknown): Promise<string | string[] | null> }>("@tauri-apps/plugin-dialog");
    const fs = await importTauri<{ readTextFile(path: string): Promise<string> }>("@tauri-apps/plugin-fs");
    const picked = await dialog.open({ multiple: true, filters: [{ name: kind.name, extensions: kind.extensions }] });
    const paths = Array.isArray(picked) ? picked : typeof picked === "string" ? [picked] : null;
    if (!paths) return null;
    return Promise.all(paths.map((p) => fs.readTextFile(p)));
  }
  return pickFilesInBrowser(kind);
}

async function importTauri<T>(spec: string): Promise<T> {
  return (await import(/* @vite-ignore */ spec)) as T;
}

function downloadInBrowser(text: string, name: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function pickInBrowser(kind: FileKind): Promise<string | null> {
  return new Promise((resolve) => {
    const input = fileInput(kind, false);
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      file.text().then(resolve).catch(() => resolve(null));
    };
    // If the dialog is dismissed there's no reliable cross-browser event; the
    // promise simply stays pending, which is fine — nothing else depends on it.
    input.click();
  });
}

function pickFilesInBrowser(kind: FileKind): Promise<string[] | null> {
  return new Promise((resolve) => {
    const input = fileInput(kind, true);
    input.onchange = () => {
      const files = Array.from(input.files ?? []);
      if (!files.length) return resolve(null);
      Promise.all(files.map((f) => f.text())).then(resolve).catch(() => resolve(null));
    };
    input.click();
  });
}

function fileInput(kind: FileKind, multiple: boolean): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "file";
  input.multiple = multiple;
  input.accept = `${kind.mime},${kind.extensions.map((e) => "." + e).join(",")}`;
  return input;
}
